import { inject, Injectable } from '@angular/core';
import { map, of, type Observable } from 'rxjs';
import { BaseApiService } from '../../../core/api/base-api.service';
import { AuthStore } from '../../../core/auth/auth.store';
import type { PaginatedMemberships } from '../../../core/api/generated/models/PaginatedMemberships';
import type { PaginatedTeams } from '../../../core/api/generated/models/PaginatedTeams';

/** A person or team a conversation can be handed off to. */
export interface DirectoryEntry {
  id: string;
  name: string;
}

/**
 * Reads the business directory (memberships, teams) for the handoff picker.
 * Business-scoped paths take the businessId from the current session.
 */
@Injectable({ providedIn: 'root' })
export class DirectoryApiService extends BaseApiService {
  private readonly authStore = inject(AuthStore);

  listMembers(): Observable<DirectoryEntry[]> {
    const businessId = this.authStore.business()?.id;
    if (!businessId) {
      return of([]);
    }
    // Backend route is nested: /v1/core/businesses/:businessId/memberships
    return this.get<PaginatedMemberships>(`/v1/core/businesses/${businessId}/memberships`, { params: { limit: 100 } }).pipe(
      map((response) =>
        response.data.map((membership) => ({
          id: membership.id,
          name: membership.user?.displayName ?? membership.user?.primaryEmail ?? `Membro ${membership.id.slice(0, 8)}`,
        })),
      ),
    );
  }

  listTeams(): Observable<DirectoryEntry[]> {
    const businessId = this.authStore.business()?.id;
    if (!businessId) {
      return of([]);
    }
    // Backend route is /v1/core/teams, scoped via the businessId query param.
    return this.get<PaginatedTeams>('/v1/core/teams', { params: { businessId, limit: 100 } }).pipe(
      map((response) => response.data.map((team) => ({ id: team.id, name: team.name }))),
    );
  }
}
